import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Eye } from "lucide-react";
import {
  Field,
  TextInput,
  TextArea,
  SelectInput,
  Toggle,
  FormActions,
  CollapsibleSection,
  ImageUploadWithAlt,
  autoSlug,
} from "../../components/FormField";
import { initialPages } from "../../lib/mockData";
import { showSuccess, showError } from "../../lib/toast";
import { HomeSections } from "./templates/HomeSections.js";
import { SolutionsSections } from "./templates/SolutionsSection";
import { ProductsSections } from "./templates/ProductsSections";
import { ServicesSections } from "./templates/ServicesSections";
import { IndustriesSections } from "./templates/IndustriesSection";
import { AboutSections } from "./templates/AboutSections";
import { ContactSections } from "./templates/ContactUs";
import { Footer } from "./templates/Footer";

const templates = [
  "Homepage",
  "Solutions",
  "Products",
  "Services",
  "Industries",
  "About",
  "Contact Us",
  "Landing",
  "Listing",
  "Overview",
  "Legal",
  "Footer",
];
const statusOptions = ["draft", "published", "scheduled"];

let pagesStore = [...initialPages];
export const getPagesStore = () => pagesStore;
export const addPage = (p) => {
  pagesStore = [...pagesStore, p];
};
export const updatePage = (p) => {
  pagesStore = pagesStore.map((x) => (x.id === p.id ? p : x));
};

export function PageForm({ mode }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const existing =
    mode === "edit" ? initialPages.find((p) => p.id === Number(id)) : null;

  const [title, setTitle] = useState(existing?.title ?? "");
  const [slug, setSlug] = useState(existing?.slug ?? "");
  const [template, setTemplate] = useState(existing?.template ?? "Homepage");
  const [status, setStatus] = useState(existing?.status ?? "draft");
  const [publishDate, setPublishDate] = useState(existing?.publishDate ?? "");
  const [slugManual, setSlugManual] = useState(false);

  // SEO
  const [seoActive, setSeoActive] = useState(true);
  const [seoTitle, setSeoTitle] = useState(existing?.seoTitle ?? "");
  const [seoDesc, setSeoDesc] = useState(existing?.seoDescription ?? "");
  const [seoOgAlt, setSeoOgAlt] = useState("");
  const [seoCanonical, setSeoCanonical] = useState("");
  const [seoIndex, setSeoIndex] = useState(true);

  // Auto-generate slug from title
  useEffect(() => {
    if (!slugManual && title) setSlug(autoSlug(title));
  }, [title, slugManual]);

  const validate = () => {
    const e = {};
    if (!title.trim()) e.title = "Title is required";
    if (!slug.trim()) e.slug = "Slug is required";
    return e;
  };

  const handleSave = (saveStatus) => {
    const e = validate();
    if (Object.keys(e).length > 0) {
      setErrors(e);
      showError("Please fix the errors");
      return;
    }
    setErrors({});
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      showSuccess(
        mode === "create" ? "Page created!" : "Page updated!",
        `"${title}" has been ${saveStatus === "published" ? "published" : "saved as draft"}.`,
      );
      navigate("/pages");
    }, 800);
  };

  const renderTemplateSections = () => {
    switch (template) {
      case "Homepage":
        return <HomeSections />;
      case "Solutions":
        return <SolutionsSections />;
      case "Products":
        return <ProductsSections />;
      case "Services":
        return <ServicesSections />;
      case "Industries":
        return <IndustriesSections />;
      case "About":
        return <AboutSections />;
      case "Contact Us":
        return <ContactSections />;
      case "Footer":
        return <Footer />;
      default:
        return null;
    }
  };

  return (
    <div className="max-w-[900px] mx-auto space-y-5">
      <div className="page-header">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/pages")}
            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted text-muted-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="page-title">
              {mode === "create" ? "Create Page" : "Edit Page"}
            </h1>
            <p className="page-subtitle">
              Template: <strong>{template}</strong>
            </p>
          </div>
        </div>
        <button className="flex items-center gap-2 text-sm font-medium text-muted-foreground border border-border rounded-lg px-3 py-2 hover:bg-muted transition-colors">
          <Eye className="w-4 h-4" /> Preview
        </button>
      </div>

      {/* Basic Info */}
      <div className="section-panel">
        <div className="section-panel-header">
          <h2 className="section-panel-title">Basic Information</h2>
        </div>
        <div className="section-panel-body space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Page Title" required error={errors.title}>
              <TextInput
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter page title"
                error={!!errors.title}
              />
            </Field>
            <Field
              label="Slug"
              required
              error={errors.slug}
              hint="Auto-generated from title, editable"
            >
              <TextInput
                value={slug}
                onChange={(e) => {
                  setSlugManual(true);
                  setSlug(e.target.value);
                }}
                placeholder="/page-slug"
                error={!!errors.slug}
              />
            </Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Template">
              <SelectInput
                value={template}
                onChange={(e) => setTemplate(e.target.value)}
                options={templates}
              />
            </Field>
            <Field label="Status">
              <SelectInput
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                options={statusOptions}
              />
            </Field>
          </div>
          {status === "scheduled" && (
            <Field label="Publish Date">
              <TextInput
                type="datetime-local"
                value={publishDate}
                onChange={(e) => setPublishDate(e.target.value)}
              />
            </Field>
          )}
        </div>
      </div>

      {/* Dynamic Template Sections */}
      {renderTemplateSections()}

      {/* SEO Section */}
      {template !== "Footer" && (
        <CollapsibleSection
          title="SEO Settings"
          active={seoActive}
          onToggleActive={setSeoActive}
        >
          <Field label="Meta Title" hint="Recommended: 50–60 characters">
            <TextInput
              value={seoTitle}
              onChange={(e) => setSeoTitle(e.target.value)}
              placeholder="Page title for search engines"
            />
          </Field>
          <Field
            label="Meta Description"
            hint="Recommended: 150–160 characters"
          >
            <TextArea
              value={seoDesc}
              onChange={(e) => setSeoDesc(e.target.value)}
              placeholder="Brief description for search results"
              maxLength={160}
            />
          </Field>
          <Field label="OG Image">
            <ImageUploadWithAlt
              label="Upload OG image"
              hint="1200×630px"
              alt={seoOgAlt}
              onAltChange={setSeoOgAlt}
            />
          </Field>
          <Field label="Canonical URL">
            <TextInput
              value={seoCanonical}
              onChange={(e) => setSeoCanonical(e.target.value)}
              placeholder="https://..."
            />
          </Field>
          <Toggle
            checked={seoIndex}
            onChange={setSeoIndex}
            label="Index this page"
            description="Allow search engines to index"
          />
        </CollapsibleSection>
      )}

      <FormActions
        onCancel={() => navigate("/pages")}
        onSaveDraft={() => handleSave("draft")}
        onPublish={() => handleSave("published")}
        loading={loading}
      />
    </div>
  );
}
